import React, { Fragment } from 'react';
import {
  customDateFormatter,
  dayNamesShort,
  intl,
  monthNames
} from '../../lib';

export interface Item {
  /**
   * Date in timeline
   */
  date: Date;
  /**
   * Content for item in timeline
   */
  content: JSX.Element | string;
}
export interface TimelineProps {
  /**
   * Array of objects {date: Date, content: JSX.Element | string }. Each element of the array represents one item on timeline
   */
  items?: Item[];
  /**
   * Display current date marker in timeline
   */
  displayCurrentPositionMarker?: boolean;
  /**
   * Additional css class for date
   */
  dateClassName?: string;
  /**
   * Additional css class for content
   */
  contentClassName?: string;
}

export function Timeline({
  items,
  contentClassName,
  dateClassName,
  displayCurrentPositionMarker,
  ...props
}: Readonly<TimelineProps>): JSX.Element {
  const parseDate = (date: Date) => {
    return {
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear()
    };
  };
  let isCurrentPositionMarkerDisplayed = false;
  return (
    <div {...props} className="ehs-timeline">
      <div className="container">
        <div className="steps">
          {items?.map((item, index) => {
            const { day, month, year } = parseDate(item.date);
            const dayName = dayNamesShort[item.date.getDay()];
            const {
              day: previousItemDay,
              month: previousItemMonth,
              year: previousItemYear
            } =
              index === 0
                ? {
                    day: null,
                    month: null,
                    year: null
                  }
                : parseDate(items[index - 1].date);

            const displayCurrentPosition =
              displayCurrentPositionMarker &&
              !isCurrentPositionMarkerDisplayed &&
              item.date.getTime() > new Date().getTime();

            const isItemGroupedWithPreviousOne =
              index === 0
                ? false
                : previousItemDay === day &&
                  previousItemMonth === month &&
                  previousItemYear === year;

            const contentClassNames = isItemGroupedWithPreviousOne
              ? 'content content-grouped'
              : 'content';

            const dateClassNames =
              item.date.getTime() < new Date().getTime()
                ? 'date past-date font-18'
                : 'date font-18';

            const dayNameClass =
              item.date.getTime() < new Date().getTime()
                ? 'day-name past'
                : 'day-name';

            const stepsContainerClassNames =
              item.date.getTime() < new Date().getTime()
                ? 'steps-container past'
                : 'steps-container';

            const stepsContainerAriaLabel =
              item.date.getTime() < new Date().getTime()
                ? 'past appointment'
                : 'upcoming appointment';

            if (displayCurrentPosition) {
              isCurrentPositionMarkerDisplayed = true;
            }
            return (
              <Fragment key={`timeline_item_${index}`}>
                {displayCurrentPosition && (
                  <div className="steps-container steps-container-current-position">
                    <div className="content line">
                      {intl(
                        'appointmentSchedulingPortlet.timeline.upcoming.appointments'
                      )}
                    </div>
                  </div>
                )}
                {index != 0 && month !== previousItemMonth && (
                  <h3 className="month-name sh-font font-22">
                    {monthNames[month]} {item.date.getUTCFullYear()}
                  </h3>
                )}
                <div
                  className={stepsContainerClassNames}
                  aria-label={stepsContainerAriaLabel}
                >
                  {!isItemGroupedWithPreviousOne && (
                    <div
                      className={
                        dateClassName
                          ? `${dateClassNames} ${dateClassName}`
                          : dateClassNames
                      }
                    >
                      <span className={dayNameClass}>{dayName}</span>
                      {customDateFormatter({
                        day: '2-digit',
                        month: 'long'
                      }).formatDate(item.date)}
                    </div>
                  )}
                  <div
                    className={
                      contentClassName
                        ? `${contentClassNames} ${contentClassName}`
                        : contentClassNames
                    }
                  >
                    {item.content}
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
